import { AmbientField } from "@/components/layout/AmbientField";
import { DemoNetworkBanner } from "@/components/layout/DemoNetworkBanner";
import { networkLabel } from "@/lib/network";
import { AGENT_LAYER_SLOGAN, ROUTES } from "@/lib/routes";
import { cn } from "@/lib/utils";
import { ArrowLeft, Bot, Sparkles, Wrench, Zap } from "lucide-react";
import { Link, NavLink, Outlet } from "react-router-dom";

const AGENT_NAV = [
	{ to: ROUTES.agents, label: "Agents", icon: Bot },
	{ to: ROUTES.skills, label: "Skills", icon: Wrench },
	{ to: ROUTES.intelligence, label: "Intelligence", icon: Sparkles },
] as const;

/**
 * Agent Layer mode — dark black+yellow chrome only.
 * Play/local sandbox banner sits at bottom (not sticky top).
 */
export function AgentShell() {
	const net = networkLabel();
	const showSandbox = net === "play" || net === "local";

	return (
		<div className="relative flex min-h-screen min-h-dvh flex-col bg-black text-yellow-400">
			<AmbientField />
			<header className="sticky top-0 z-40 border-b border-yellow-500/30 bg-black/95 backdrop-blur-xl">
				<div className="container mx-auto flex flex-wrap items-center gap-3 px-3 py-3 sm:px-4">
					<div className="flex min-w-0 items-center gap-2">
						<span className="flex size-9 shrink-0 items-center justify-center rounded-md border border-yellow-500/50 bg-yellow-400/15">
							<Zap className="size-5" />
						</span>
						<div className="min-w-0">
							<p className="font-mono text-[11px] font-bold tracking-[0.14em]">
								AGENT LAYER
							</p>
							<p className="truncate font-mono text-[10px] text-yellow-400/70">
								{AGENT_LAYER_SLOGAN}
							</p>
						</div>
					</div>

					<nav
						className="order-3 flex w-full flex-wrap items-center gap-1 sm:order-none sm:ml-4 sm:w-auto sm:flex-1"
						aria-label="Agent Layer"
					>
						{AGENT_NAV.map(({ to, label, icon: Icon }) => (
							<NavLink
								key={to}
								to={to}
								end={to === ROUTES.agents}
								className={({ isActive }) =>
									cn(
										"inline-flex min-h-10 items-center gap-1.5 rounded-md border px-3 font-mono text-xs font-bold",
										isActive
											? "border-yellow-400 bg-yellow-400 text-black"
											: "border-yellow-500/40 bg-black text-yellow-400 hover:bg-yellow-400/10",
									)
								}
							>
								<Icon className="size-3.5" aria-hidden />
								{label}
							</NavLink>
						))}
					</nav>

					<Link
						to={ROUTES.borrow}
						className="ml-auto inline-flex min-h-10 items-center gap-1.5 rounded-md border border-yellow-500/50 px-3 font-mono text-xs font-bold text-yellow-400 hover:bg-yellow-400/10"
					>
						<ArrowLeft className="size-3.5" aria-hidden />
						Back to DeFi
					</Link>
				</div>
			</header>

			<main className="min-w-0 flex-1 pb-16">
				<Outlet />
			</main>

			<footer className="mt-auto border-t border-yellow-500/20">
				<div className="container mx-auto flex flex-wrap items-center justify-between gap-2 px-4 py-3 font-mono text-[10px] text-yellow-400/60">
					<span>{AGENT_LAYER_SLOGAN}</span>
					<div className="flex gap-3">
						<Link to={ROUTES.borrow} className="hover:text-yellow-400">
							DeFi platform
						</Link>
						<Link to={ROUTES.docs} className="hover:text-yellow-400">
							Docs
						</Link>
					</div>
				</div>
				{/* Desktop: full strip; mobile: soft one-liner */}
				{showSandbox ? (
					<>
						<div className="hidden md:block">
							<DemoNetworkBanner compact variant="full" />
						</div>
						<div className="md:hidden">
							<DemoNetworkBanner variant="mobile-soft" />
						</div>
					</>
				) : null}
			</footer>
		</div>
	);
}
